import React, { useEffect ,useState} from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { ToastProvider } from './contexts/ToastContext';
import './styles/shell.css';
import './styles/themes.css';

import Login from './pages/Login';
import Welcome from './pages/Welcome';
import Dashboard from './pages/Dashboard';
import ProtectedRoute from './components/ProtectedRoute';
import PinProtectedRoute from './components/PinProtectedRoute';
import Unauthorized from './pages/Unauthorized';
import EvaluationDashboard from './pages/EvaluationDashboard';
import CreateProject from './pages/CreateProject'; // This is a full page, not modal here
import MyProfile from './pages/MyProfile';
import Education from './pages/Education';
import ClubProjects from './pages/ClubProjects';
import ClubStatistics from './pages/ClubStatistics';
import Tickets from './pages/Tickets';
import AdminConsole from './pages/AdminConsole';
import SessionManagement from './pages/SessionManagement';
import DeviceManagement from './pages/DeviceManagement';
import ReportingHub from './pages/ReportingHub';
import MemberDatabase from './pages/MemberDatabase';
import Installation from './pages/Installation';
import AddRegistration from './pages/AddRegistration';
import ProjectDetails from './pages/ProjectDetail';
import ForgotPassword from './pages/ForgotPassword';
import PathManagement from './pages/PathManagement';
import EducationAddQuizess from './pages/EducationAddQuizess';
import EducationStatistics from './pages/EducationStatistics';
import EducationLeaderBoard  from './pages/EducationLeaderBoard';
import CategorySelection from './pages/CategorySelection';
import TicketsAdmin from './pages/TicketAdmin';
import ProjectNeedAssessment from './pages/ProjectNeedAssessment';
import ClubCalander from './pages/ClubCalander';
import InstallationReport from './pages/installation-report';
import BanUser from './pages/BanUser';
import Attendance from './pages/AttendancePage';
import ScanQRCode from './pages/ScanQRPage';
import EventManagement from './pages/Events';
import MemberDatabaseQR from './pages/MemberDatabaseQR';
import Preloader from "./components/Preloader";
import TreasurerReport from "./pages/TreasurerReport";
import SecretaryReport from "./pages/SecretaryReport";
import TitleSync from "./components/TitleSync";
import NotificationSettings from "./pages/NotificationSettings";
import { PROJECT_REPORT_MANAGER_ROLES, REPORTING_HUB_ROLES, ADMIN_ROLES } from './config/roles';
import { getToken, getCurrentUser } from './utils/api';
import { isStandalonePwa } from './utils/pwa';
import { notificationPermissionState, requestNotificationPermission, subscribeToPush } from './utils/push';
import { useTheme } from './theme/ThemeProvider';

// react-toastify's `theme` prop only accepts 'light'|'dark'|'colored' -- map
// the active portal theme onto whichever of those two reads correctly
// against that theme's page background, so toasts aren't dark-on-dark or
// light-on-light for the portal's light themes.
const LIGHT_BACKGROUND_THEMES = new Set(['executive-light', 'leo-legacy']);

// Installed-as-an-app users who are already logged in should never see the
// login form again on relaunch -- skip straight to the home page. Browser
// tab users (not installed) are unaffected and always see Login normally.
function HomeOrLogin() {
  if (isStandalonePwa() && getToken() && getCurrentUser()) {
    return <Navigate to="/home" replace />;
  }
  return <Login />;
}

function App() {
  // Handle theme initialization and changes
  useEffect(() => {
    // Set dark theme by default if no theme is saved
    const savedTheme = localStorage.getItem('theme') || 'dark';
    const root = document.documentElement;
    
    // Remove all theme classes first
    root.classList.remove('light', 'dark', 'contrast');
    
    // Apply the saved theme (or default to dark)
    if (savedTheme === 'system') {
      const systemTheme = window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
      root.classList.add(systemTheme);
      document.documentElement.setAttribute('data-theme', 'system');
    } else {
      root.classList.add(savedTheme);
      document.documentElement.setAttribute('data-theme', savedTheme);
    }
    
    // Save the theme if it wasn't set
    if (!localStorage.getItem('theme')) {
      localStorage.setItem('theme', 'dark');
    }

    // Handle system theme changes
    const handleSystemThemeChange = (e) => {
      const currentTheme = localStorage.getItem('theme');
      if (currentTheme === 'system') {
        root.classList.remove('light', 'dark');
        const systemTheme = e.matches ? 'dark' : 'light';
        root.classList.add(systemTheme);
      }
    };

    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    mediaQuery.addEventListener('change', handleSystemThemeChange);

    return () => {
      mediaQuery.removeEventListener('change', handleSystemThemeChange);
    };
  }, []);

  const { theme } = useTheme();
  const toastTheme = LIGHT_BACKGROUND_THEMES.has(theme) ? 'light' : 'dark';

   const [boot, setBoot] = useState(true);
  useEffect(() => {
    const id = setTimeout(() => setBoot(false), 1200); // ~1.2s
    return () => clearTimeout(id);
  }, []);

  // Ask for notification permission right when the installed app is opened,
  // instead of only via the dismissible Home-page banner -- that banner
  // still shows as a fallback if this silently doesn't prompt (some
  // browsers only show the permission dialog for a "genuine" click). Once
  // granted (now or on an earlier launch), (re)register this device's push
  // subscription with the backend so new events/projects/board birthdays
  // arrive as real notifications even with the app fully closed.
  useEffect(() => {
    if (!isStandalonePwa()) return;
    (async () => {
      let permission = notificationPermissionState();
      if (permission === 'default') {
        permission = await requestNotificationPermission();
      }
      if (permission === 'granted') {
        // This is the path that actually runs on every subsequent launch
        // once permission was already granted (the Home page's "Enable"
        // banner only shows while permission is still 'default', so after
        // the first grant this silent effect is the only thing that ever
        // retries the subscription). Surface failures visibly -- on mobile
        // there's no console to check.
        const result = await subscribeToPush();
        if (!result.ok) {
          toast.error(`Push setup failed: ${result.error}`, { toastId: 'push-subscribe-failed' });
        }
      }
    })();
  }, []);

  // Handle theme initialization and changes
  useEffect(() => {
    const savedTheme = localStorage.getItem('theme') || 'dark';
    const root = document.documentElement;
    root.classList.remove('light', 'dark', 'contrast');

    if (savedTheme === 'system') {
      const systemTheme = window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
      root.classList.add(systemTheme);
      document.documentElement.setAttribute('data-theme', 'system');
    } else {
      root.classList.add(savedTheme);
      document.documentElement.setAttribute('data-theme', savedTheme);
    }

    if (!localStorage.getItem('theme')) {
      localStorage.setItem('theme', 'dark');
    }

    const handleSystemThemeChange = (e) => {
      const currentTheme = localStorage.getItem('theme');
      if (currentTheme === 'system') {
        root.classList.remove('light', 'dark');
        const systemTheme = e.matches ? 'dark' : 'light';
        root.classList.add(systemTheme);
      }
    };

    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    mediaQuery.addEventListener('change', handleSystemThemeChange);
    return () => mediaQuery.removeEventListener('change', handleSystemThemeChange);
  }, []);


  return (
    <ToastProvider>
      <Preloader show={boot} />
    <Router>
      <TitleSync />
      <ToastContainer
        position="top-right"
        autoClose={5000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        theme={toastTheme}
      />

      <div className="leo-shell-v2">
      <Routes>
        {/* <Route path="/" element={<Dashboard />} /> */}
        <Route path="/" element={<HomeOrLogin />} />
        <Route path="/login" element={<HomeOrLogin />} />
        {/* <Route path="/welcome" element={<Welcome />} /> */}
        <Route path="/forgot-password" element={<ForgotPassword />} />

        

        <Route path="/create-project" element={
          <ProtectedRoute allowedRoles={['System Admin','president','secretary','Club Member','Club Performence Evaluator','President Program Coordinator','Club Director','treasurer','Club Secretary','Vice President','Club President','Club President Program Coordinator','Activity Director','Team Head','Club IT Department','Installation sub-committee member','Club Reporting Sub-Committee','Club Committee Member','Club Member',"Co - Asst. Secretary",
            "Co - Asst. Treasurer",] }>
            <CreateProject />
          </ProtectedRoute>
        } />
        <Route path="/profile" element={
          <ProtectedRoute allowedRoles={['System Admin','president','secretary','Club Member','Club Performence Evaluator','President Program Coordinator','Club Director','treasurer','Club Secretary','Vice President','Club President','Club President Program Coordinator','Activity Director','Team Head','Club IT Department','Installation sub-committee member','Club Reporting Sub-Committee','Club Committee Member','Club Member',"Co - Asst. Secretary",
            "Co - Asst. Treasurer",] }>
            <MyProfile />
          </ProtectedRoute>
        } />
        
        <Route path="/education" element={
          <ProtectedRoute allowedRoles={['System Admin','president','secretary','Club Member','Club Performence Evaluator','President Program Coordinator','Club Director','treasurer','Club Secretary','Vice President','Club President','Club President Program Coordinator','Activity Director','Team Head','Club IT Department','Installation sub-committee member','Club Reporting Sub-Committee','Club Committee Member',"Co - Asst. Secretary",
            "Co - Asst. Treasurer",] }>
            <Education />
          </ProtectedRoute>
        } />
        
        <Route path="/tickets" element={
          <ProtectedRoute allowedRoles={['System Admin','president','secretary','Club Member','Club Performence Evaluator','President Program Coordinator','Club Director',' Treasurer','Club Secretary','Vice President','Club President','Club President Program Coordinator','Activity Director','Team Head','Club IT Department','Installation sub-committee member','Club Reporting Sub-Committee','Club Committee Member',"Co - Asst. Secretary",
            "Co - Asst. Treasurer",] }>
            <Tickets />
          </ProtectedRoute>
        } />
        <Route path="/admin" element={
          <ProtectedRoute allowedRoles={['System Admin',"president" ] }>
            <AdminConsole />
          </ProtectedRoute>
        } />
        <Route path="/device-management" element={
          <ProtectedRoute allowedRoles={['System Admin' ,"president"]}>
            <DeviceManagement />
          </ProtectedRoute>
        } />
        <Route path="/add-registration" element={
          <ProtectedRoute allowedRoles={['System Admin','president','secretary','Club Performence Evaluator','President Program Coordinator','Club Director','treasurer','Club Secretary','Vice President','Club President','Club President Program Coordinator','Activity Director','Team Head','Club IT Department','Installation sub-committee member','Club Reporting Sub-Committee','Club Committee Member']}>
            <AddRegistration />
          </ProtectedRoute>
        } />

        {/* Sensitive: Only admin, president, secretary can access */}
        <Route path="/evaluation" element={
          <ProtectedRoute allowedRoles={['System Admin','president','secretary','Club Performence Evaluator','President Program Coordinator','Club Director','treasurer','Club Secretary','Vice President','Club President','Club President Program Coordinator','Activity Director','Team Head','Club IT Department','Club Reporting Sub-Committee','Club Committee Member']}>
            <EvaluationDashboard />
          </ProtectedRoute>
        } /> 
        <Route path="/dashboard" element={
          <ProtectedRoute allowedRoles={['System Admin','president','secretary','Club Member','Club Performence Evaluator','President Program Coordinator','Club Director','treasurer','Club Secretary','Vice President','Club President','Club President Program Coordinator','Activity Director','Team Head','Club IT Department','Club Reporting Sub-Committee','Club Committee Member']}>
            <Dashboard />
          </ProtectedRoute>
        } /> 
        <Route path="/club-dashboard" element={
          <ProtectedRoute allowedRoles={['System Admin','president','secretary','Club Member','Club Performence Evaluator','President Program Coordinator','Club Director','treasurer','Club Secretary','Vice President','Club President','Club President Program Coordinator','Activity Director','Team Head','Club IT Department','Club Reporting Sub-Committee','Club Committee Member']}>
            <ClubStatistics />
          </ProtectedRoute>
        } /> 
        <Route path="/club-projects" element={
          <ProtectedRoute allowedRoles={['System Admin','president','secretary','Club Performence Evaluator','President Program Coordinator','Club Director',' Treasurer','Vice President','Club President Program Coordinator']}>
            <ClubProjects />
          </ProtectedRoute>
        } /> 

        <Route path="/project/:id" element={<ProjectDetails />} />
        <Route path="/project/:projectId/tresurer" element={
          <ProtectedRoute allowedRoles={PROJECT_REPORT_MANAGER_ROLES}>
            <TreasurerReport />
          </ProtectedRoute>
        } />
        <Route path="/project/:id/treasurer" element={
          <ProtectedRoute allowedRoles={PROJECT_REPORT_MANAGER_ROLES}>
            <TreasurerReport />
          </ProtectedRoute>
        } />
        <Route path="/project/:id/secretary-report" element={
          <ProtectedRoute allowedRoles={PROJECT_REPORT_MANAGER_ROLES}>
            <SecretaryReport />
          </ProtectedRoute>
        } />

        <Route path="/club-statistics" element={
          <ProtectedRoute allowedRoles={['System Admin','president','secretary','Club Member','Club Performence Evaluator','President Program Coordinator','Club Director',' Treasurer','Vice President','Club President Program Coordinator','Activity Director','Team Head','Club IT Department',]}>
            <ClubStatistics />
          </ProtectedRoute>
        } /> 
        <Route path="/reporting-hub" element={
          <ProtectedRoute allowedRoles={REPORTING_HUB_ROLES}>
            <ReportingHub />
          </ProtectedRoute>
        } /> 
        <Route path="/member-database" element={
          <PinProtectedRoute allowedRoles={['System Admin','president','secretary', 'Club Performence Evaluator','President Program Coordinator','Club Director','treasurer',' Vice President','Team Head']}>
            <MemberDatabase />
          </PinProtectedRoute>
        } /> 
        <Route path="/installation" element={
          <ProtectedRoute allowedRoles={['System Admin','president','secretary','Treasurer','Vice President','Installation sub-committee member','President Program Coordinator']}>
            <Installation />
          </ProtectedRoute>
        } /> 
        <Route path="/session-management" element={
          <ProtectedRoute allowedRoles={['System Admin',"president"]}>
            <SessionManagement />
          </ProtectedRoute>
        } /> 
        <Route path="/path-management" element={
          <ProtectedRoute allowedRoles={['System Admin' ,"president"]}>
            <PathManagement />
          </ProtectedRoute>
        } />
        <Route path="/notification-settings" element={
          <ProtectedRoute allowedRoles={ADMIN_ROLES}>
            <NotificationSettings />
          </ProtectedRoute>
        } />

        <Route path="/education-statistics" element={
          <ProtectedRoute allowedRoles={['System Admin',"president",
            "Vice President",
            "Secretary",
            "Treasurer"]}>
            <EducationStatistics />
          </ProtectedRoute>
        } /> 
        <Route path="/education-leaderboard" element={
          <ProtectedRoute allowedRoles={['System Admin' ,"president",
            "Vice President"]}>
            <EducationLeaderBoard />
          </ProtectedRoute>
        } /> 
        <Route path="/education-add-quiz-question" element={
          <ProtectedRoute allowedRoles={['System Admin' ,"president",
            "Vice President"]}>
            <EducationAddQuizess />
          </ProtectedRoute>
        } /> 
         
        <Route path="/tickets-admin" element={
          <ProtectedRoute allowedRoles={['System Admin' ,"president",
            "Vice President","President Program Coordinator","Club Director"]}>
            <TicketsAdmin />
          </ProtectedRoute>
        } /> 
        <Route path="/category-selection" element={
          <ProtectedRoute allowedRoles={['System Admin',"president","Vice President","President Program Coordinator","Club Director"]}>
            <CategorySelection />
          </ProtectedRoute>
        } /> 
        
        <Route path="/home" element={
          <ProtectedRoute allowedRoles={['',
            "Club Performence Evaluator",
            "System Admin",
            "president",
            "Vice President",
            "Secretary",
            "Treasurer",
            "Co - Asst. Secretary",
            "Co - Asst. Treasurer",
            "Chief Editor",
            "Past President",
            "President Program Coordinator",
            "Activity Director",
            "Club Director",
            "Past Board Member",
            "Team Head",
            "IT Department",
            "Installation sub-committee member",
            "Reporting Sub-Committee",
            "Committee Member",
            "Club Member"
          ]}>
            
            <Dashboard/>
          </ProtectedRoute>
        } /> 
        <Route path="/ProjectNeedAssessment" element={
          <ProtectedRoute allowedRoles={['System Admin' ,"President"]}>
            <ProjectNeedAssessment />
          </ProtectedRoute>
        } /> 
        <Route path="/club-calander" element={
          <ProtectedRoute allowedRoles={["Club Performence Evaluator",
            "System Admin",
            "President",
            "Vice President",
            "Secretary",
            "Treasurer",
            "Co - Asst. Secretary",
            "Co - Asst. Treasurer",
            "Chief Editor",
            "Past President",
            "President Program Coordinator",
            "Activity Director",
            "Club Director",
            "Past Board Member",
            "Team Head",
            "IT Department",
            "Installation sub-committee member",
            "Reporting Sub-Committee",
            "Committee Member",
            "Club Member" ]}>
            <ClubCalander />
          </ProtectedRoute>
        } /> 
        <Route path="/installation-report" element={
          <ProtectedRoute allowedRoles={["Club Performence Evaluator",
            "System Admin",
            "President",
            "Vice President",
            "Secretary",
            "Treasurer",
            "Co - Asst. Secretary",
            "Co - Asst. Treasurer",
             ]}>
            <InstallationReport />
          </ProtectedRoute>
        } /> 
        <Route path="/attendance" element={
          <ProtectedRoute allowedRoles={["Club Performence Evaluator",
            "System Admin",
            "President",
            "Vice President",
            "Secretary",
            "Treasurer",
            "Co - Asst. Secretary",
            "Co - Asst. Treasurer",
             ]}>
            <Attendance />
          </ProtectedRoute>
        } /> 
        <Route path="/scan-qr-code" element={
          <ProtectedRoute allowedRoles={["Club Performence Evaluator",
            "System Admin",
            "President",
            "Vice President",
            "Secretary",
            "Treasurer",
            "Co - Asst. Secretary",
            "Co - Asst. Treasurer",
             ]}>
            <ScanQRCode />
          </ProtectedRoute>
        } /> 
        <Route path="/events" element={
          <ProtectedRoute allowedRoles={["Club Performence Evaluator",
            "System Admin",
            "President",
            "Vice President",
            "Secretary",
            "Treasurer",
            "Co - Asst. Secretary",
            "Co - Asst. Treasurer",
             ]}>
            <EventManagement />
          </ProtectedRoute>
        } />
        <Route path="/members" element={
          <ProtectedRoute allowedRoles={["Club Performence Evaluator",
            "System Admin",
            "President",
            "Vice President",
            "Secretary",
            "Treasurer",
            
             ]}>
            <MemberDatabaseQR />
          </ProtectedRoute>
        } /> 

        






        <Route path="/unauthorized" element={<Unauthorized />} />
        <Route path="/restricted" element={
          <ProtectedRoute allowedRoles={["Banned"
             ]}>
            <BanUser />
          </ProtectedRoute>
        }  />
      </Routes>
      </div>
    </Router>
    </ToastProvider>
  );
}

export default App;
